import { supabase } from "./churchSupabaseClient";
import { fetchOrganizationAccess } from "./churchOrganizationAccessService";
import { getStoredChurchOrgUserSession } from "./churchOrgUserAuthService";
import { getCurrentServiceId, loadServiceItems, syncHymnBlocksWithWeeklyPlan } from "./serviceService";

const HYMNS_TABLE = "church_hymns";
const SERVICE_ITEMS_TABLE = "service_items";
const CHURCH_HYMN_BUCKET = "church-hymn-files";
const LOCAL_TEST_HYMNS_MANIFEST = "/local-hymn-test/hymns/hymns-manifest.json";
const ENABLE_LOCAL_TEST_HYMNS = process.env.REACT_APP_ENABLE_LOCAL_HYMN_TESTS === "true";
const WEEKLY_HYMN_SERVICE_PREFIX = "hymns_week";
const WEEKLY_HYMN_LABEL_STORAGE_PREFIX = "oikos.church.weekly-hymn-labels";
const HYMN_FAVORITES_STORAGE_PREFIX = "oikos.church.hymn-favorites";
export const WEEKLY_HYMN_SLOT_COUNT = 6;
export const DEFAULT_WEEKLY_HYMN_SLOT_LABELS = [
  "Opening Hymn",
  "Hymn 2",
  "Hymn 3",
  "Hymn 4",
  "Closing Hymn",
  "Invitation Song",
];

function isMissingRelationError(error) {
  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    (typeof error?.message === "string" &&
      error.message.toLowerCase().includes("does not exist"))
  );
}

function normalizeSlides(rawSlides = [], hymn = {}) {
  if (!Array.isArray(rawSlides) || rawSlides.length === 0) {
    return [
      {
        id: `${hymn.songNumber || hymn.song_number || "song"}-title`,
        title: hymn.title || "Untitled Hymn",
        body: hymn.songNumber || hymn.song_number
          ? `Song #${hymn.songNumber || hymn.song_number}`
          : "Imported hymn record",
      },
    ];
  }

  return rawSlides.map((slide, index) => ({
    id: slide.id || `${hymn.songNumber || hymn.song_number || "song"}-${index + 1}`,
    title: slide.title || hymn.title || "Untitled Hymn",
    body: slide.body || slide.text || "",
    imageUrl: slide.imageUrl || slide.image_url || "",
    imagePath: slide.imagePath || slide.image_path || "",
    storageBucket: slide.storageBucket || slide.storage_bucket || "global-hymn-files",
    isEndOfSong:
      typeof slide.isEndOfSong === "boolean"
        ? slide.isEndOfSong
        : typeof slide.is_end_of_song === "boolean"
          ? slide.is_end_of_song
          : index === rawSlides.length - 1,
  }));
}

async function hydrateHymnSlideUrls(hymns = []) {
  const groupedPaths = new Map();

  hymns.forEach((hymn) => {
    (hymn.slides || []).forEach((slide) => {
      if (!slide?.imagePath || slide?.imageUrl) {
        return;
      }

      const bucket = slide.storageBucket || "global-hymn-files";
      const existing = groupedPaths.get(bucket) || [];
      existing.push(slide.imagePath);
      groupedPaths.set(bucket, existing);
    });
  });

  if (groupedPaths.size === 0) {
    return hymns;
  }

  const signedUrlMap = new Map();

  await Promise.all(
    Array.from(groupedPaths.entries()).map(async ([bucket, paths]) => {
      const uniquePaths = Array.from(new Set(paths));
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrls(uniquePaths, 60 * 60 * 24 * 30);

      if (error) {
        console.error(`Hymn slide URL signing error for ${bucket}:`, error);
        return;
      }

      (data || []).forEach((entry, index) => {
        if (entry?.signedUrl) {
          signedUrlMap.set(`${bucket}:${uniquePaths[index]}`, entry.signedUrl);
        }
      });
    })
  );

  return hymns.map((hymn) => ({
    ...hymn,
    slides: (hymn.slides || []).map((slide) => {
      if (!slide?.imagePath || slide?.imageUrl) {
        return slide;
      }

      const bucket = slide.storageBucket || "global-hymn-files";

      return {
        ...slide,
        imageUrl: signedUrlMap.get(`${bucket}:${slide.imagePath}`) || "",
      };
    }),
  }));
}

function normalizeHymn(row = {}) {
  return {
    ...row,
    accountId: row.account_id || "",
    isGlobal: row.is_global !== false,
    songNumber: row.song_number || "",
    title: row.title || "",
    titleSource: row.title_source || "unknown",
    sourceFileName: row.source_file_name || "",
    sourceRelativePath: row.source_relative_path || "",
    sourceExtension: row.source_extension || "",
    filePath: row.file_path || "",
    fileUrl: row.file_url || "",
    slideCount: row.slide_count || 0,
    slides: normalizeSlides(row.slides || [], row),
    needsReview: row.needs_review === true,
    licenseVerified: row.license_verified === true,
    isAdminApproved: row.is_admin_approved === true,
    approvedBy: row.approved_by || "",
    approvedAt: row.approved_at || "",
    isActive: row.is_active !== false,
  };
}

async function loadLocalTestHymns() {
  if (process.env.NODE_ENV === "production" || !ENABLE_LOCAL_TEST_HYMNS) {
    return [];
  }

  try {
    const response = await fetch(LOCAL_TEST_HYMNS_MANIFEST, {
      cache: "no-store",
    });

    if (!response.ok) {
      return [];
    }

    const manifest = await response.json();
    return Array.isArray(manifest?.hymns)
      ? manifest.hymns.map((row) => normalizeHymn(row))
      : [];
  } catch (_error) {
    return [];
  }
}

function getUserId(userOrId) {
  return typeof userOrId === "string" ? userOrId : userOrId?.id || "";
}

function getFavoriteUserId(userOrId) {
  if (typeof userOrId === "string") return userOrId;

  return (
    userOrId?.churchOrgSession?.orgUser?.id ||
    userOrId?.orgUser?.id ||
    userOrId?.id ||
    ""
  );
}

function getChurchOrgSessionToken(userOrId) {
  if (typeof userOrId === "object") {
    return userOrId?.churchOrgSession?.sessionToken || "";
  }

  const storedSession = getStoredChurchOrgUserSession();
  return storedSession?.orgUser?.id === userOrId ? storedSession.sessionToken || "" : "";
}

function isMissingRpcError(error) {
  const message = String(error?.message || "").toLowerCase();
  return error?.code === "42883" || message.includes("could not find the function");
}

async function getChurchAccountId(userOrId) {
  const userId = getUserId(userOrId);
  const sessionAccountId = typeof userOrId === "object"
    ? userOrId?.churchOrgSession?.account?.id ||
      userOrId?.account?.id ||
      userOrId?.organization?.id ||
      ""
    : "";

  if (sessionAccountId) return sessionAccountId;
  if (!userId) return "";

  try {
    const access = await fetchOrganizationAccess(userId, "church");
    return access?.account?.id || "";
  } catch (_error) {
    return "";
  }
}

function sanitizeFileName(name = "hymn-file") {
  return String(name || "hymn-file")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120) || "hymn-file";
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getCurrentHymnWeekStartDate(date = new Date()) {
  const weekStart = new Date(date);
  weekStart.setHours(0, 0, 0, 0);
  const daysUntilSunday = (7 - weekStart.getDay()) % 7;
  weekStart.setDate(weekStart.getDate() + daysUntilSunday);
  return formatLocalDate(weekStart);
}

export function getHymnWeekServiceId(weekStart = getCurrentHymnWeekStartDate()) {
  return `${WEEKLY_HYMN_SERVICE_PREFIX}_${String(weekStart || getCurrentHymnWeekStartDate())}`;
}

export function buildDefaultWeeklyHymnSlots() {
  return buildWeeklyHymnSlotsWithLabels(DEFAULT_WEEKLY_HYMN_SLOT_LABELS);
}

export function buildWeeklyHymnSlotsWithLabels(labels = []) {
  const sourceLabels = Array.isArray(labels) ? labels : [];
  const slotCount = sourceLabels.length
    ? Math.max(1, Math.min(WEEKLY_HYMN_SLOT_COUNT, sourceLabels.length))
    : WEEKLY_HYMN_SLOT_COUNT;

  return Array.from({ length: slotCount }, (_unused, index) => ({
    slotIndex: index + 1,
    label: String(sourceLabels[index] || DEFAULT_WEEKLY_HYMN_SLOT_LABELS[index] || `Hymn ${index + 1}`).trim() || `Hymn ${index + 1}`,
    hymnId: "",
  }));
}

export function normalizeWeeklyHymnSlotLabels(labels = []) {
  return buildWeeklyHymnSlotsWithLabels(labels).map((slot) => slot.label);
}

function getWeeklyHymnLabelStorageKey(accountId = "", userId = "") {
  return `${WEEKLY_HYMN_LABEL_STORAGE_PREFIX}:${accountId || userId || "default"}`;
}

function getHymnFavoritesStorageKey(accountId = "", userId = "") {
  return `${HYMN_FAVORITES_STORAGE_PREFIX}:${accountId || "default"}:${userId || "anonymous"}`;
}

function normalizeFavoriteHymnIds(hymnIds = []) {
  return Array.from(new Set(
    (Array.isArray(hymnIds) ? hymnIds : [])
      .map((hymnId) => String(hymnId || "").trim())
      .filter(Boolean)
  ));
}

export async function loadFavoriteHymnIds(userOrId) {
  if (typeof window === "undefined") return [];

  const [accountId, favoriteUserId] = await Promise.all([
    getChurchAccountId(userOrId),
    Promise.resolve(getFavoriteUserId(userOrId)),
  ]);

  try {
    const stored = window.localStorage.getItem(getHymnFavoritesStorageKey(accountId, favoriteUserId));
    const parsed = stored ? JSON.parse(stored) : [];
    return normalizeFavoriteHymnIds(parsed);
  } catch (_error) {
    return [];
  }
}

export async function saveFavoriteHymnIds(userOrId, hymnIds = []) {
  const normalizedIds = normalizeFavoriteHymnIds(hymnIds);
  if (typeof window === "undefined") return normalizedIds;

  const [accountId, favoriteUserId] = await Promise.all([
    getChurchAccountId(userOrId),
    Promise.resolve(getFavoriteUserId(userOrId)),
  ]);

  window.localStorage.setItem(
    getHymnFavoritesStorageKey(accountId, favoriteUserId),
    JSON.stringify(normalizedIds)
  );

  return normalizedIds;
}

function readStoredWeeklyHymnSlotLabels(accountId = "", userId = "") {
  if (typeof window === "undefined") {
    return normalizeWeeklyHymnSlotLabels();
  }

  try {
    const stored = window.localStorage.getItem(getWeeklyHymnLabelStorageKey(accountId, userId));
    const parsed = stored ? JSON.parse(stored) : null;
    return Array.isArray(parsed) ? normalizeWeeklyHymnSlotLabels(parsed) : normalizeWeeklyHymnSlotLabels();
  } catch (_error) {
    return normalizeWeeklyHymnSlotLabels();
  }
}

function writeStoredWeeklyHymnSlotLabels(accountId = "", userId = "", labels = []) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      getWeeklyHymnLabelStorageKey(accountId, userId),
      JSON.stringify(normalizeWeeklyHymnSlotLabels(labels))
    );
  } catch (_error) {
    // Local storage is only a fallback for offline or missing preference RPCs.
  }
}

export async function loadChurchHymns(userOrId) {
  const userId = getUserId(userOrId);
  const accountId = await getChurchAccountId(userOrId);
  const localTestHymns = await loadLocalTestHymns();
  let adminAccessRow = null;
  let accessError = null;

  if (userId) {
    const result = await supabase
      .from("user_access")
      .select("user_id")
      .eq("user_id", userId)
      .eq("platform", "admin")
      .eq("mode", "default")
      .eq("has_access", true)
      .maybeSingle();

    adminAccessRow = result.data;
    accessError = result.error;
  }

  if (accessError && !isMissingRelationError(accessError)) {
    throw accessError;
  }

  const canApprove = Boolean(adminAccessRow);

  let query = supabase
    .from(HYMNS_TABLE)
    .select("*")
    .eq("is_active", true)
    .order("song_number", { ascending: true })
    .order("title", { ascending: true });

  if (accountId) {
    query = query.or(`is_global.eq.true,account_id.eq.${accountId}`);
  } else {
    query = query.eq("is_global", true);
  }

  const { data, error } = await query;

  if (error) {
    if (isMissingRelationError(error)) {
      return { hymns: localTestHymns, canApprove };
    }

    throw error;
  }

  const hymns = Array.isArray(data) ? data.map((row) => normalizeHymn(row)) : [];

  return {
    canApprove,
    hymns: [...localTestHymns, ...(await hydrateHymnSlideUrls(hymns))],
  };
}

export async function loadHymnUsageStats() {
  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .select("source_id, payload")
    .eq("source_app", "hymns")
    .limit(1000);

  if (error) {
    if (isMissingRelationError(error)) {
      return {};
    }

    throw error;
  }

  return (data || []).reduce((counts, item) => {
    const hymnId = item.source_id || item.payload?.hymnId || "";
    if (!hymnId) return counts;
    return {
      ...counts,
      [hymnId]: (counts[hymnId] || 0) + 1,
    };
  }, {});
}

export function searchChurchHymns(hymns = [], query = "") {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const normalizedSongNumberQuery = normalizedQuery.replace(/^#/, "").replace(/\D/g, "");
  const hasSongNumberQuery = normalizedSongNumberQuery.length > 0;

  if (!normalizedQuery) {
    return hymns;
  }

  return hymns
    .map((hymn) => {
      const title = (hymn.title || "").toLowerCase();
      const songNumber = String(hymn.songNumber ?? hymn.song_number ?? "").toLowerCase();
      const normalizedSongNumber = songNumber.replace(/\D/g, "");
      const fileName = (hymn.sourceFileName || "").toLowerCase();
      const numberExactMatch = hasSongNumberQuery && normalizedSongNumber === normalizedSongNumberQuery;
      const numberStartsMatch = hasSongNumberQuery && normalizedSongNumber.startsWith(normalizedSongNumberQuery);
      const numberIncludesMatch = hasSongNumberQuery && normalizedSongNumber.includes(normalizedSongNumberQuery);
      const titleMatch = title.includes(normalizedQuery);
      const fileMatch = fileName.includes(normalizedQuery);

      if (!numberIncludesMatch && !titleMatch && !fileMatch) {
        return null;
      }

      return {
        hymn,
        rank: numberExactMatch ? 0 : numberStartsMatch ? 1 : numberIncludesMatch ? 2 : titleMatch ? 3 : 4,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank)
    .map((result) => result.hymn);
}

function buildServiceItemFromHymn(hymn, serviceId, sortOrder, slotLabel = "") {
  const slides = normalizeSlides(hymn.slides, hymn);

  return {
    id: crypto.randomUUID(),
    service_id: serviceId,
    type: "hymn",
    source_app: "hymns",
    source_id: hymn.id,
    sort_order: sortOrder,
    title: hymn.title || `Song ${hymn.songNumber || ""}`.trim(),
    payload: {
      hymnId: hymn.id,
      songNumber: hymn.songNumber || "",
      hymnTitle: hymn.title || "",
      slotIndex: sortOrder,
      slotLabel: slotLabel || DEFAULT_WEEKLY_HYMN_SLOT_LABELS[sortOrder - 1] || `Hymn ${sortOrder}`,
      sourceFileName: hymn.sourceFileName || "",
      sourceRelativePath: hymn.sourceRelativePath || "",
      slides,
    },
    created_at: new Date().toISOString(),
  };
}

function normalizeWeeklyPlanItem(row = {}) {
  const payload = row.payload || row.result_payload || {};
  const sortOrder = row.sort_order || row.result_sort_order || row.sortOrder || payload.slotIndex || 1;

  return {
    id: row.id || `${row.plan_id || row.result_plan_id || "plan"}-${row.hymn_id || row.result_hymn_id || row.hymnId || sortOrder}`,
    service_id: getHymnWeekServiceId(row.week_start || row.result_week_start || row.weekStart),
    type: "hymn",
    source_app: "hymns",
    source_id: row.hymn_id || row.result_hymn_id || row.hymnId || payload.hymnId || "",
    sort_order: sortOrder,
    title: row.title || row.result_title || payload.hymnTitle || "",
    payload: {
      ...payload,
      slotIndex: Number(payload.slotIndex || sortOrder) || 1,
      slotLabel:
        payload.slotLabel ||
        row.slot_label ||
        row.result_slot_label ||
        DEFAULT_WEEKLY_HYMN_SLOT_LABELS[(Number(sortOrder) || 1) - 1] ||
        `Hymn ${sortOrder}`,
    },
    created_at: row.created_at || row.result_created_at || row.createdAt || "",
  };
}

function buildWeeklyHymnSlotsFromItems(items = [], hymnById = new Map(), labels = DEFAULT_WEEKLY_HYMN_SLOT_LABELS) {
  const slots = buildWeeklyHymnSlotsWithLabels(labels);

  items.forEach((item) => {
    const requestedSlotIndex = Number(item.payload?.slotIndex || item.sort_order || 1) || 1;
    if (requestedSlotIndex < 1 || requestedSlotIndex > slots.length) {
      return;
    }

    const slotIndex = Math.max(1, Math.min(slots.length, requestedSlotIndex));
    const hymnId = item.source_id || item.payload?.hymnId || "";
    const hymn = hymnById.get(hymnId);

    slots[slotIndex - 1] = {
      ...slots[slotIndex - 1],
      label: labels[slotIndex - 1] || item.payload?.slotLabel || slots[slotIndex - 1].label,
      hymnId,
      hymn: hymn || null,
    };
  });

  return slots;
}

function normalizeWeeklyHymnSlots(input = [], hymnById = new Map()) {
  const source = Array.isArray(input) ? input : [];
  const looksLikeSlots = source.some((entry) =>
    Object.prototype.hasOwnProperty.call(entry || {}, "hymnId") ||
    Object.prototype.hasOwnProperty.call(entry || {}, "slotIndex") ||
    Object.prototype.hasOwnProperty.call(entry || {}, "label")
  );
  const defaults = looksLikeSlots
    ? buildWeeklyHymnSlotsWithLabels(source.map((slot, index) => slot?.label || DEFAULT_WEEKLY_HYMN_SLOT_LABELS[index]))
    : buildDefaultWeeklyHymnSlots();

  if (!looksLikeSlots) {
    source.slice(0, WEEKLY_HYMN_SLOT_COUNT).forEach((hymn, index) => {
      defaults[index] = {
        ...defaults[index],
        hymnId: hymn?.id || "",
        hymn: hymn || null,
      };
    });
    return defaults;
  }

  source.slice(0, defaults.length).forEach((slot, index) => {
    const slotIndex = Number(slot?.slotIndex || index + 1) || index + 1;
    const hymnId = slot?.hymnId || slot?.hymn?.id || "";
    const boundedSlotIndex = Math.max(1, Math.min(defaults.length, slotIndex));
    defaults[boundedSlotIndex - 1] = {
      ...defaults[boundedSlotIndex - 1],
      label: String(slot?.label || defaults[boundedSlotIndex - 1]?.label || `Hymn ${boundedSlotIndex}`).trim() || `Hymn ${boundedSlotIndex}`,
      hymnId,
      hymn: slot?.hymn || hymnById.get(hymnId) || null,
    };
  });

  return defaults;
}

export async function loadWeeklyHymnSlotLabels(userOrId) {
  const userId = getUserId(userOrId);
  const accountId = await getChurchAccountId(userOrId);
  const sessionToken = getChurchOrgSessionToken(userOrId);
  const fallbackLabels = readStoredWeeklyHymnSlotLabels(accountId, userId);

  if (!accountId) {
    return fallbackLabels;
  }

  const { data, error } = await supabase.rpc("church_get_weekly_hymn_preferences", {
    p_account_id: accountId,
    p_session_token: sessionToken,
  });

  if (error) {
    if (isMissingRpcError(error) || isMissingRelationError(error)) {
      return fallbackLabels;
    }

    throw error;
  }

  const row = Array.isArray(data) ? data[0] : data;
  const labels = normalizeWeeklyHymnSlotLabels(row?.slot_labels || row?.result_slot_labels || []);
  writeStoredWeeklyHymnSlotLabels(accountId, userId, labels);
  return labels;
}

export async function saveWeeklyHymnSlotLabels(userOrId, labels = []) {
  const userId = getUserId(userOrId);
  const accountId = await getChurchAccountId(userOrId);
  const sessionToken = getChurchOrgSessionToken(userOrId);
  const normalizedLabels = normalizeWeeklyHymnSlotLabels(labels);

  writeStoredWeeklyHymnSlotLabels(accountId, userId, normalizedLabels);

  if (!accountId) {
    return normalizedLabels;
  }

  const { error } = await supabase.rpc("church_save_weekly_hymn_preferences", {
    p_account_id: accountId,
    p_slot_labels: normalizedLabels,
    p_session_token: sessionToken,
  });

  if (error && !isMissingRpcError(error) && !isMissingRelationError(error)) {
    throw error;
  }

  return normalizedLabels;
}

export async function addHymnToService(userId, hymn) {
  const serviceId = getCurrentServiceId();
  const current = await loadServiceItems(userId);
  const nextSortOrder = (current.items || []).length + 1;
  const serviceItem = buildServiceItemFromHymn(hymn, serviceId, nextSortOrder);

  const { error } = await supabase.from(SERVICE_ITEMS_TABLE).insert(serviceItem);

  if (error && !isMissingRelationError(error)) {
    throw error;
  }

  return {
    serviceId,
    serviceItem,
  };
}

export async function loadWeeklyHymnPlan(userOrId, weekStart = getCurrentHymnWeekStartDate(), knownHymns = null) {
  const serviceId = getHymnWeekServiceId(weekStart);
  const accountId = await getChurchAccountId(userOrId);
  const sessionToken = getChurchOrgSessionToken(userOrId);
  const hymns = Array.isArray(knownHymns)
    ? knownHymns
    : (await loadChurchHymns(userOrId)).hymns || [];
  const hymnById = new Map(hymns.map((hymn) => [hymn.id, hymn]));
  const slotLabels = await loadWeeklyHymnSlotLabels(userOrId);

  if (accountId) {
    const { data: planRows, error: planError } = await supabase.rpc("church_get_weekly_hymn_plan", {
      p_account_id: accountId,
      p_week_start: weekStart,
      p_session_token: sessionToken,
    });

    if (!planError) {
      const items = Array.isArray(planRows) ? planRows.map((row) => normalizeWeeklyPlanItem(row)) : [];
      const slots = buildWeeklyHymnSlotsFromItems(items, hymnById, slotLabels);
      const plannedHymns = items
        .map((item) => hymnById.get(item.source_id || item.payload?.hymnId || ""))
        .filter(Boolean);

      return {
        serviceId,
        weekStart,
        hymns: plannedHymns,
        items,
        slots,
        slotLabels,
      };
    }

    if (!isMissingRpcError(planError) && !isMissingRelationError(planError)) {
      throw planError;
    }
  }

  const { data, error } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .select("*")
    .eq("service_id", serviceId)
    .eq("source_app", "hymns")
    .eq("type", "hymn")
    .order("sort_order", { ascending: true });

  if (error) {
    if (isMissingRelationError(error)) {
      return { serviceId, weekStart, hymns: [], items: [], slots: buildWeeklyHymnSlotsWithLabels(slotLabels), slotLabels };
    }

    throw error;
  }

  const plannedHymns = (data || [])
    .map((item) => hymnById.get(item.source_id || item.payload?.hymnId || ""))
    .filter(Boolean);
  const normalizedItems = (data || []).map((item) => normalizeWeeklyPlanItem(item));

  return {
    serviceId,
    weekStart,
    hymns: plannedHymns,
    items: normalizedItems,
    slots: buildWeeklyHymnSlotsFromItems(normalizedItems, hymnById, slotLabels),
    slotLabels,
  };
}

export async function saveWeeklyHymnPlan(userOrId, weekStart = getCurrentHymnWeekStartDate(), hymnsOrSlots = []) {
  const userId = getUserId(userOrId);
  const accountId = await getChurchAccountId(userOrId);
  const sessionToken = getChurchOrgSessionToken(userOrId);
  const serviceId = getHymnWeekServiceId(weekStart);
  const hymnById = new Map(
    (Array.isArray(hymnsOrSlots) ? hymnsOrSlots : [])
      .map((entry) => entry?.hymn || entry)
      .filter((hymn) => hymn?.id)
      .map((hymn) => [hymn.id, hymn])
  );
  const selectedSlots = normalizeWeeklyHymnSlots(hymnsOrSlots, hymnById);
  const slotLabels = normalizeWeeklyHymnSlotLabels(selectedSlots.map((slot) => slot.label));
  const filledSlots = selectedSlots.filter((slot) => slot.hymnId);
  const selectedHymns = filledSlots
    .map((slot) => slot.hymn || hymnById.get(slot.hymnId))
    .filter(Boolean);

  if (accountId) {
    await saveWeeklyHymnSlotLabels(userOrId, slotLabels);

    const { error: planError } = await supabase.rpc("church_save_weekly_hymn_plan", {
      p_account_id: accountId,
      p_week_start: weekStart,
      p_slots: filledSlots.map((slot) => ({
        hymn_id: slot.hymnId,
        slot_index: slot.slotIndex,
        slot_label: slot.label,
      })),
      p_session_token: sessionToken,
    });

    if (!planError) {
      const savedPlan = await loadWeeklyHymnPlan(userOrId, weekStart, selectedHymns);
      await syncHymnBlocksWithWeeklyPlan(userId, savedPlan);
      return savedPlan;
    }

    if (!isMissingRpcError(planError) && !isMissingRelationError(planError)) {
      throw planError;
    }
  }

  await saveWeeklyHymnSlotLabels(userOrId, slotLabels);

  const { error: deleteError } = await supabase
    .from(SERVICE_ITEMS_TABLE)
    .delete()
    .eq("service_id", serviceId)
    .eq("source_app", "hymns")
    .eq("type", "hymn");

  if (deleteError && !isMissingRelationError(deleteError)) {
    throw deleteError;
  }

  const serviceItems = filledSlots
    .map((slot) => slot.hymn || hymnById.get(slot.hymnId))
    .filter(Boolean)
    .map((hymn, index) =>
      buildServiceItemFromHymn(hymn, serviceId, filledSlots[index]?.slotIndex || index + 1, filledSlots[index]?.label || "")
    );

  if (serviceItems.length > 0) {
    const { error: insertError } = await supabase
      .from(SERVICE_ITEMS_TABLE)
      .insert(serviceItems);

    if (insertError && !isMissingRelationError(insertError)) {
      throw insertError;
    }
  }

  const savedPlan = await loadWeeklyHymnPlan(userOrId, weekStart, selectedHymns);
  await syncHymnBlocksWithWeeklyPlan(userId, savedPlan);
  return savedPlan;
}

export async function listWeeklyHymnPlans(userOrId, limit = 26) {
  const accountId = await getChurchAccountId(userOrId);
  const sessionToken = getChurchOrgSessionToken(userOrId);

  if (!accountId) {
    return [];
  }

  const { data, error } = await supabase.rpc("church_list_weekly_hymn_plans", {
    p_account_id: accountId,
    p_limit: limit,
    p_session_token: sessionToken,
  });

  if (error) {
    if (isMissingRpcError(error) || isMissingRelationError(error)) {
      return [];
    }

    throw error;
  }

  return (Array.isArray(data) ? data : []).map((row) => ({
    id: row.plan_id || row.result_plan_id || "",
    accountId: row.account_id || row.result_account_id || "",
    weekStart: row.week_start || row.result_week_start || "",
    hymnCount: row.hymn_count || row.result_hymn_count || 0,
    updatedAt: row.updated_at || row.result_updated_at || "",
  }));
}

export async function loadUpcomingWeeklyHymnPlan(userOrId, knownHymns = null) {
  const plans = await listWeeklyHymnPlans(userOrId, 52);
  const currentWeekStart = getCurrentHymnWeekStartDate();
  const upcomingPlan = plans
    .filter((plan) => plan.weekStart && plan.weekStart >= currentWeekStart)
    .sort((left, right) => String(left.weekStart).localeCompare(String(right.weekStart)))[0];
  const fallbackPlan = plans[0];
  const selectedPlan = upcomingPlan || fallbackPlan;

  if (!selectedPlan?.weekStart) {
    return loadWeeklyHymnPlan(userOrId, currentWeekStart, knownHymns);
  }

  return loadWeeklyHymnPlan(userOrId, selectedPlan.weekStart, knownHymns);
}

export async function loadUpcomingWeeklyHymnPlanForDisplay(publicCode = "") {
  const { data, error } = await supabase.rpc("church_get_upcoming_weekly_hymn_plan_for_display", {
    p_public_code: publicCode,
  });

  if (error) {
    if (isMissingRpcError(error) || isMissingRelationError(error)) {
      return { weekStart: getCurrentHymnWeekStartDate(), items: [], hymns: [] };
    }

    throw error;
  }

  const rows = Array.isArray(data) ? data : [];

  return {
    weekStart: rows[0]?.week_start || rows[0]?.result_week_start || getCurrentHymnWeekStartDate(),
    items: rows.map((row) => normalizeWeeklyPlanItem(row)),
    hymns: [],
    slots: buildWeeklyHymnSlotsFromItems(rows.map((row) => normalizeWeeklyPlanItem(row))),
  };
}

export async function updateChurchHymnTitle(hymnId, title) {
  const normalizedTitle = String(title || "").trim();

  const { data, error } = await supabase
    .from(HYMNS_TABLE)
    .update({
      title: normalizedTitle,
      title_source: normalizedTitle ? "manual" : "unknown",
    })
    .eq("id", hymnId)
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  const [hydrated] = await hydrateHymnSlideUrls([normalizeHymn(data)]);
  return hydrated;
}

export async function createChurchHymn(userOrId, { songNumber, title, file, files } = {}) {
  const userId = getUserId(userOrId);
  const createdBy = typeof userOrId === "object" && userOrId?.isChurchOrgUser
    ? null
    : userId || null;
  const accountId = await getChurchAccountId(userOrId);
  const normalizedSongNumber = String(songNumber || "").trim();
  const normalizedTitle = String(title || "").trim();

  if (!normalizedSongNumber) {
    throw new Error("Song number is required.");
  }

  if (!normalizedTitle) {
    throw new Error("Hymn name is required.");
  }

  if (!accountId) {
    throw new Error("A Church organization is required before uploading hymns.");
  }

  let filePath = "";
  let fileUrl = "";
  let sourceFileName = "";
  let sourceExtension = "";
  let slides = [];
  const imageFiles = Array.isArray(files) && files.length > 0
    ? files
    : file
      ? [file]
      : [];

  if (imageFiles.length > 0) {
    const uploadBatchId = Date.now();
    sourceFileName = imageFiles.length === 1
      ? imageFiles[0].name || "hymn-slide"
      : `${imageFiles.length} hymn slide images ${uploadBatchId}`;
    sourceExtension = imageFiles.length === 1 && sourceFileName.includes(".")
      ? sourceFileName.split(".").pop().toLowerCase()
      : "images";
    const uploadedSlides = await Promise.all(
      imageFiles.map(async (imageFile, index) => {
        const slideFileName = imageFile.name || `slide-${index + 1}.png`;
        const slidePath = `${accountId}/${normalizedSongNumber || "song"}-${uploadBatchId}/${String(index + 1).padStart(3, "0")}-${sanitizeFileName(slideFileName)}`;

        const { error: uploadError } = await supabase.storage
          .from(CHURCH_HYMN_BUCKET)
          .upload(slidePath, imageFile, {
            cacheControl: "3600",
            upsert: false,
            contentType: imageFile.type || "image/png",
          });

        if (uploadError) {
          throw uploadError;
        }

        return {
          id: `${normalizedSongNumber || "song"}-slide-${index + 1}`,
          title: normalizedTitle,
          body: "",
          imagePath: slidePath,
          storageBucket: CHURCH_HYMN_BUCKET,
          isEndOfSong: index === imageFiles.length - 1,
        };
      })
    );

    slides = uploadedSlides;
    filePath = uploadedSlides[0]?.imagePath || "";
  } else if (file) {
    sourceFileName = file.name || "hymn-file";
    sourceExtension = sourceFileName.includes(".")
      ? sourceFileName.split(".").pop().toLowerCase()
      : "";
    filePath = `${accountId}/${Date.now()}-${sanitizeFileName(sourceFileName)}`;

    const { error: uploadError } = await supabase.storage
      .from(CHURCH_HYMN_BUCKET)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }
  }

  const { data, error } = await supabase
    .from(HYMNS_TABLE)
    .insert({
      account_id: accountId,
      created_by: createdBy,
      is_global: false,
      song_number: normalizedSongNumber,
      title: normalizedTitle,
      title_source: "manual",
      source_file_name: sourceFileName,
      source_extension: sourceExtension,
      file_path: filePath,
      file_url: fileUrl,
      slide_count: slides.length,
      slides,
      needs_review: imageFiles.length === 0,
      license_verified: false,
      is_admin_approved: false,
      is_active: true,
    })
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return normalizeHymn(data);
}

export async function setChurchHymnApproval(userId, hymn, isApproved) {
  const { data: accessRow, error: accessError } = await supabase
    .from("user_access")
    .select("user_id")
    .eq("user_id", userId)
    .eq("platform", "admin")
    .eq("mode", "default")
    .eq("has_access", true)
    .maybeSingle();

  if (accessError && !isMissingRelationError(accessError)) {
    throw accessError;
  }

  if (!accessRow) {
    throw new Error("Only approved admin users can approve hymns for service use.");
  }

  const updates = {
    is_admin_approved: isApproved,
    approved_by: isApproved ? userId : null,
    approved_at: isApproved ? new Date().toISOString() : null,
  };

  const { data, error } = await supabase
    .from(HYMNS_TABLE)
    .update(updates)
    .eq("id", hymn.id)
    .select("*")
    .single();

  if (error) {
    throw error;
  }

  return normalizeHymn(data);
}
